import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';
import {
    createAssociatedTokenAccountIdempotentInstruction,
    createCloseAccountInstruction,
    createTransferInstruction,
    getAssociatedTokenAddressSync,
    NATIVE_MINT,
    TOKEN_PROGRAM_ID,
    unpackAccount,
} from '@solana/spl-token';
import {
    AccountInfo,
    ComputeBudgetProgram,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';

import { BN } from '@coral-xyz/anchor';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import { getZeroslotTipInstruction, sendZeroslotTransaction } from '../0slot/0slot';
import {
    getPdaCreatorVault,
    getPdaLaunchpadAuth,
    getPdaLaunchpadConfigId,
    getPdaLaunchpadPoolId,
    getPdaLaunchpadVaultId,
    getPdaPlatformVault,
    LaunchConstantProductCurve,
    LAUNCHPAD_PROGRAM,
    LaunchpadPool,
    sellExactInInstruction,
} from '@raydium-io/raydium-sdk-v2';
import { Section } from '../Components/Section';
import { LetsBonkIcon } from '../Components/icons/LetsBonkIcon';
import { PumpFunIcon } from '../Components/icons/PumpFunIcon';
import { PumpSwapIcon } from '../Components/icons/PumpSwapIcon';
import { RaydiumIcon } from '../Components/icons/RaydiumIcon';
import { CopyToClipboard } from '../Components/CopyToClipboard';
import { DexIcon } from '../Components/icons/DexIcon';
import * as Form from '../Components/form';
import * as pumpfun from '../pumpfun/pumpfun';
import * as pumpswap from '../pumpswap/pumpswap';
import { SupportedDex } from '../types';
import { calculateRequiredMicroLamportsFromTxCost } from '../utils/solana/calculate-required-microLamports-from-tx-cost';

interface InjectedToken {
    address: string;
    name: string;
    symbol: string;
    imageSrc: string;
}

interface Props {
    solPrivateKey: string;
    solSniperPrivateKey: string;
    zeroSlotSellTip: number;
    sellTxCost: number;
    solConnection: Connection;
    deployedTokens: InjectedToken[];
    purchasedTokens: InjectedToken[];
}

const emptyLocationState = {};

const dexIconsMap: { [key in SupportedDex]: React.ComponentType } = {
    bonk: LetsBonkIcon,
    pumpfun: PumpFunIcon,
    pumpswap: PumpSwapIcon,
    raydium: RaydiumIcon,
};

const renderDexIcon = (dex: SupportedDex, props: React.HTMLAttributes<HTMLImageElement>) => {
    const Icon = dexIconsMap[dex];

    return <Icon {...props} />;
};

export function Result({ solPrivateKey, solSniperPrivateKey, zeroSlotSellTip, solConnection, deployedTokens, purchasedTokens, sellTxCost }: Props) {
    let locationState = useLocation().state ?? emptyLocationState;
    const [initBuyDeployer, setInitBuyDeployer] = useState<number | undefined>(undefined);
    const [initBuySniper, setInitBuySniper] = useState<number | undefined>(undefined);

    const [autoSellTimeLeft, setAutoSellTimeLeft] = useState<number | undefined>(undefined);
    const [autoSellTimer, setAutoSellTimer] = useState<boolean>(true);

    const [autoSellProfitGoal, setAutoSellProfitGoal] = useState<number | undefined>(undefined);
    const [autoSellProfit, setAutoSellProfit] = useState<boolean>(true);

    const [imageSrc, setImageSrc] = useState<string | null>(null);

    const [mint, setMint] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [symbol, setSymbol] = useState<string>('');
    const [dex, setDex] = useState<SupportedDex | undefined>(undefined);
    const [tokenCreator, setTokenCreator] = useState<string | undefined>(undefined);

    const [tokenValueDeployer, setTokenValueDeployer] = useState<number>(0);

    const [tokenValueSniper, setTokenValueSniper] = useState<number>(0);

    const [tokenBalanceDeployer, setTokenBalanceDeployer] = useState<string>('0');
    const [transferPercentDeployer, setTransferPercentDeployer] = useState<number>(100);
    const [transferWalletDeployer, setTransferWalletDeployer] = useState<string>('');

    const [tokenBalanceSniper, setTokenBalanceSniper] = useState<string>('0');
    const [transferPercentSniper, setTransferPercentSniper] = useState<number>(100);
    const [transferWalletSniper, setTransferWalletSniper] = useState<string>('');

    if (locationState && !initBuyDeployer) {
        setInitBuyDeployer(locationState.initBuyDeployer);
        setInitBuySniper(locationState.initBuySniper);
        setAutoSellTimeLeft(locationState.autoSellDelay);
        setAutoSellProfitGoal(locationState.autoSellProfit);
    }

    useEffect(() => {
        if (!mint) return;
        if (dex === 'pumpswap') return;
        if (dex === 'raydium') return;

        interface CheckResult {
            type: 'pumpfun' | 'pumpswap' | 'bonk' | 'raydium';
            creator: string;
        }

        const checkPumpfun = async (): Promise<CheckResult | undefined> => {
            const bondingCurve = pumpfun.getBondingCurvePubkey(new PublicKey(mint));
            const accountData = await solConnection.getAccountInfo(bondingCurve, 'processed');
            if (!accountData) return undefined;

            const decoded = pumpfun.decodeBondingCurve(accountData);
            return {
                type: decoded.complete ? 'pumpswap' : 'pumpfun',
                creator: decoded.creator.toBase58(),
            };
        };

        const checkBonk = async (): Promise<CheckResult | undefined> => {
            const poolId = getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey;
            const account = await solConnection.getAccountInfo(poolId, 'processed');
            if (!account) return undefined;

            const decoded = LaunchpadPool.decode(account.data);

            return {
                type: decoded.status === 0 ? 'bonk' : 'raydium',
                creator: decoded.creator.toBase58(),
            };
        };

        const updateStatus = async () => {
            let platform: 'pumpfun' | 'pumpswap' | 'bonk' | 'raydium' | undefined = dex;
            let creator: string | undefined = tokenCreator;

            if (!platform || platform === 'pumpfun') {
                const result = await checkPumpfun();
                if (result) {
                    platform = result.type;
                    creator = result.creator;
                }
            }

            if (!platform || platform === 'bonk') {
                const result = await checkBonk();
                if (result) {
                    platform = result.type;
                    creator = result.creator;
                }
            }

            setDex(platform);
            setTokenCreator(creator);
        };

        updateStatus();
        const intervalId = setInterval(updateStatus, 5000);

        return () => clearInterval(intervalId);
    }, [dex, mint, solConnection, tokenCreator]);

    useEffect(() => {
        const walletsToTrack = [
            {
                privateKey: solPrivateKey,
                setBalance: setTokenBalanceDeployer,
            },
            {
                privateKey: solSniperPrivateKey,
                setBalance: setTokenBalanceSniper,
            },
        ];

        const subs: number[] = [];

        walletsToTrack.forEach(({ privateKey, setBalance }) => {
            try {
                if (!privateKey || !mint) return;

                const wallet = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(privateKey)));
                const tokenAccount = getAssociatedTokenAddressSync(new PublicKey(mint), wallet.publicKey);

                const fetchBalance = () => {
                    solConnection
                        .getTokenAccountBalance(tokenAccount, 'processed')
                        .then((res) => {
                            setBalance(res.value.uiAmountString || '0');
                        })
                        .catch(() => {
                            setBalance('0');
                        });
                };

                fetchBalance();
                setTimeout(fetchBalance, 2000);

                const subId = solConnection.onAccountChange(
                    tokenAccount,
                    async (accountInfo) => {
                        try {
                            const account = unpackAccount(tokenAccount, accountInfo);
                            setBalance((Number(account.amount) / 1e6).toString());
                        } catch {
                            setBalance('0');
                        }
                    },
                    { commitment: 'processed' },
                );

                subs.push(subId);
            } catch (err) {
                console.error('Error tracking token balance:', err);
            }
        });

        return () => {
            subs.forEach((subId) => {
                solConnection.removeAccountChangeListener(subId);
            });
        };
    }, [solPrivateKey, solSniperPrivateKey, mint, solConnection]);

    const sell = useCallback(
        async function sell(percent: number, privateKey: string) {
            if (!mint) {
                return toast.error('No mint selected!');
            }
            if (!tokenCreator) {
                return toast.error('No token creator found!');
            }

            try {
                const seller = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(privateKey)));
                const ata = getAssociatedTokenAddressSync(new PublicKey(mint), seller.publicKey);

                const balanceResponce = (await solConnection.getTokenAccountBalance(ata)).value;
                if (!balanceResponce.uiAmount) {
                    console.log(balanceResponce.amount);
                    toast.error('No balance');
                    return;
                }
                toast.info(`Selling ${(balanceResponce.uiAmount * percent) / 100} ${symbol}...`);

                console.log(balanceResponce.amount);
                const sellAmount = Math.floor((Number(balanceResponce.amount) * percent) / 100);

                const sellIxs: TransactionInstruction[] = [];
                if (dex === 'pumpfun') {
                    sellIxs.push(
                        await pumpfun.getPumpSellInstructions(seller, new PublicKey(mint), BigInt(sellAmount), BigInt(0), new PublicKey(tokenCreator)),
                    );
                } else if (dex === 'pumpswap') {
                    sellIxs.push((await pumpswap.getSellIxs(solConnection, seller.publicKey, new PublicKey(mint), new BN(sellAmount), 0))[1]);
                } else if (dex === 'bonk') {
                    sellIxs.push(
                        sellExactInInstruction(
                            LAUNCHPAD_PROGRAM,
                            seller.publicKey,
                            getPdaLaunchpadAuth(LAUNCHPAD_PROGRAM).publicKey,
                            getPdaLaunchpadConfigId(LAUNCHPAD_PROGRAM, NATIVE_MINT, 0, 0).publicKey,
                            new PublicKey('FfYek5vEz23cMkWsdJwG2oa6EphsvXSHrGpdALN4g6W1'), //letsbonk
                            getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey,
                            getAssociatedTokenAddressSync(new PublicKey(mint), seller.publicKey),
                            getAssociatedTokenAddressSync(NATIVE_MINT, seller.publicKey),
                            getPdaLaunchpadVaultId(
                                LAUNCHPAD_PROGRAM,
                                getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey,
                                new PublicKey(mint),
                            ).publicKey,
                            getPdaLaunchpadVaultId(
                                LAUNCHPAD_PROGRAM,
                                getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey,
                                NATIVE_MINT,
                            ).publicKey,
                            new PublicKey(mint),
                            NATIVE_MINT,
                            TOKEN_PROGRAM_ID,
                            TOKEN_PROGRAM_ID,
                            getPdaPlatformVault(LAUNCHPAD_PROGRAM, new PublicKey('FfYek5vEz23cMkWsdJwG2oa6EphsvXSHrGpdALN4g6W1'), NATIVE_MINT).publicKey,
                            getPdaCreatorVault(LAUNCHPAD_PROGRAM, new PublicKey(tokenCreator), NATIVE_MINT).publicKey,
                            sellAmount,
                            0,
                        ),
                    );
                }

                const { blockhash } = await solConnection.getLatestBlockhash('confirmed');
                console.log('zeroslot tip', zeroSlotSellTip);
                const tx = new VersionedTransaction(
                    new TransactionMessage({
                        payerKey: seller.publicKey,
                        recentBlockhash: blockhash,
                        instructions: [
                            createAssociatedTokenAccountIdempotentInstruction(
                                seller.publicKey,
                                getAssociatedTokenAddressSync(NATIVE_MINT, seller.publicKey),
                                seller.publicKey,
                                NATIVE_MINT,
                            ),
                            ...sellIxs,
                            createCloseAccountInstruction(getAssociatedTokenAddressSync(NATIVE_MINT, seller.publicKey), seller.publicKey, seller.publicKey),
                            ComputeBudgetProgram.setComputeUnitPrice({ microLamports: calculateRequiredMicroLamportsFromTxCost(sellTxCost, 150_000) }),
                            ComputeBudgetProgram.setComputeUnitLimit({ units: 150_000 }),
                            getZeroslotTipInstruction(seller.publicKey, zeroSlotSellTip * LAMPORTS_PER_SOL),
                        ],
                    }).compileToV0Message(),
                );
                tx.sign([seller]);
                const signature = await sendZeroslotTransaction(tx);
                toast.success(`Selling tx sent ${signature}`);
            } catch (error) {
                toast.error('Error selling');
                console.error('Error selling:', error);
            }
        },
        [mint, tokenCreator, solConnection, symbol, dex, zeroSlotSellTip, sellTxCost],
    );

    useEffect(() => {
        if (!mint) return;

        const wallets = [
            {
                name: 'deployer',
                privateKey: solPrivateKey,
                tokenBalance: tokenBalanceDeployer,
                setTokenValue: setTokenValueDeployer,
            },
            {
                name: 'sniper',
                privateKey: solSniperPrivateKey,
                tokenBalance: tokenBalanceSniper,
                setTokenValue: setTokenValueSniper,
            },
        ].filter((w) => w.privateKey);

        const subs: number[] = [];

        if (dex === 'pumpfun') {
            const bondingCurvePubkey = pumpfun.getBondingCurvePubkey(new PublicKey(mint));

            const updateWallets = (accountData: AccountInfo<Buffer>) => {
                try {
                    const bondingCurveData = pumpfun.decodeBondingCurve(accountData);
                    wallets.forEach(({ tokenBalance, setTokenValue }) => {
                        if (!tokenBalance) return;
                        const amount = new BN(Math.floor(Number(tokenBalance) * 1e6));
                        const tokenValue = pumpfun.getMinSolAmount(amount, bondingCurveData, 0);
                        setTokenValue(tokenValue.toNumber());
                    });
                } catch (e) {
                    console.error('Error decoding PUMPFUN curve:', e);
                }
            };

            solConnection.getAccountInfo(bondingCurvePubkey, 'processed').then((acc) => {
                if (acc) updateWallets(acc);
            });

            const subId = solConnection.onAccountChange(bondingCurvePubkey, updateWallets, { commitment: 'processed' });
            subs.push(subId);
        }

        if (dex === 'bonk') {
            const poolId = getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(mint), NATIVE_MINT).publicKey;

            const updateWallets = (data: Buffer) => {
                try {
                    const poolData = LaunchpadPool.decode(data);
                    wallets.forEach(({ tokenBalance, setTokenValue }) => {
                        if (!tokenBalance) return;
                        const amount = new BN(Math.floor(Number(tokenBalance) * 1e6 * 0.99));
                        const tokenValue = LaunchConstantProductCurve.sellExactIn({
                            poolInfo: poolData,
                            amount,
                        });
                        setTokenValue(tokenValue.toNumber());
                    });
                } catch (e) {
                    console.error('Error decoding BONK curve:', e);
                }
            };

            solConnection.getAccountInfo(poolId, 'processed').then((acc) => {
                if (acc) updateWallets(acc.data);
            });

            const subId = solConnection.onAccountChange(poolId, (accData) => updateWallets(accData.data), { commitment: 'processed' });
            subs.push(subId);
        } else if (dex === 'pumpswap') {
            pumpswap.getPumpswapPoolId(solConnection, new PublicKey(mint)).then((poolId) => {
                if (!poolId) {
                    console.log('No PUMPSWAP pool found');
                    return;
                }
                const updateWallets = (accountData: Buffer) => {
                    try {
                        const poolData = pumpswap.decodePumpswapPool(accountData);
                        pumpswap.fetchPumpswapReserves(solConnection, poolData).then((reserves) => {
                            wallets.forEach(({ tokenBalance, setTokenValue }) => {
                                if (!tokenBalance) return;
                                const amount = new BN(Math.floor(Number(tokenBalance) * 1e6));
                                const tokenValue = pumpswap.getSellBaseInput(amount, 0, reserves.baseReserves, reserves.quoteReserves, new BN(20), new BN(5));
                                setTokenValue(tokenValue.toNumber());
                            });
                        });
                    } catch (e) {
                        console.error('Error decoding PUMPSWAP pool:', e);
                    }
                };

                solConnection.getAccountInfo(poolId, 'processed').then((acc) => {
                    if (acc) updateWallets(acc.data);
                });

                const subId = solConnection.onAccountChange(poolId, (accData) => updateWallets(accData.data), { commitment: 'processed' });
                subs.push(subId);
            });
        }
        return () => {
            subs.forEach((subId) => {
                solConnection.removeAccountChangeListener(subId);
            });
        };
    }, [solPrivateKey, solSniperPrivateKey, mint, dex, tokenBalanceDeployer, tokenBalanceSniper, solConnection]);

    // TODO: refactor so that sell happens on autoSellProfit or initBuyDeployer or autoSellProfitGoal changes insead of effect
    useEffect(() => {
        if (!autoSellProfit) return;
        if (!initBuyDeployer) return;
        if (!autoSellProfitGoal) return;

        const currentProfitPercentDeployer = ((tokenValueDeployer / LAMPORTS_PER_SOL - initBuyDeployer) / initBuyDeployer) * 100;
        if (currentProfitPercentDeployer < autoSellProfitGoal) return;

        console.log('Auto sell triggered', currentProfitPercentDeployer, autoSellProfit);
        toast.info('Auto sell on profit triggered');
        sell(100, solPrivateKey);
        setAutoSellProfit(false);
    }, [autoSellProfit, autoSellProfitGoal, tokenValueDeployer, sell, solPrivateKey, initBuyDeployer]);

    useEffect(() => {
        if (!autoSellTimer) return;
        if (!initBuyDeployer) return;

        const interval = setInterval(() => {
            setAutoSellTimeLeft((prev) => {
                if (!prev) return;
                if (prev > 1) return prev - 1;

                clearInterval(interval);
                setAutoSellTimer(false);
                toast.info('Auto sell triggered');
                sell(100, solPrivateKey);
                return 0;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [autoSellTimer, initBuyDeployer, sell, solPrivateKey]);

    async function transfer(senderPK: string, receiver: string, percent: number) {
        try {
            toast.info(`Transferring ${percent}% to ${receiver}...`);
            const senderKeypair = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(senderPK)));
            console.log('Sender Address:', senderKeypair.publicKey.toBase58());
            console.log('Receiver Address:', receiver);
            const senderAta = getAssociatedTokenAddressSync(new PublicKey(mint), senderKeypair.publicKey);
            const balance = await solConnection.getTokenAccountBalance(senderAta, 'processed');
            if (!balance.value) {
                return toast.error('No balance');
            }
            const amount = Math.floor((Number(balance.value.amount) * percent) / 100);

            const tx = new VersionedTransaction(
                new TransactionMessage({
                    payerKey: senderKeypair.publicKey,
                    recentBlockhash: (await solConnection.getLatestBlockhash('confirmed')).blockhash,
                    instructions: [
                        createAssociatedTokenAccountIdempotentInstruction(
                            senderKeypair.publicKey,
                            getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(receiver)),
                            new PublicKey(receiver),
                            new PublicKey(mint),
                        ),
                        createTransferInstruction(
                            getAssociatedTokenAddressSync(new PublicKey(mint), senderKeypair.publicKey),
                            getAssociatedTokenAddressSync(new PublicKey(mint), new PublicKey(receiver)),
                            senderKeypair.publicKey,
                            amount,
                        ),
                        getZeroslotTipInstruction(senderKeypair.publicKey, zeroSlotSellTip * LAMPORTS_PER_SOL),
                    ],
                }).compileToV0Message(),
            );
            tx.sign([senderKeypair]);

            const sign = await sendZeroslotTransaction(tx);

            toast.success(`Transfer tx sent ${sign}`);
        } catch (error) {
            toast.error('Error transferring');
            console.error('Error transferring:', error);
        }
    }

    function injectToken(token: InjectedToken) {
        setMint(token.address);
        setName(token.name);
        setSymbol(token.symbol);
        setImageSrc(token.imageSrc);
        setDex(undefined);
        setTokenValueDeployer(0);
        setTokenValueSniper(0);
        setInitBuyDeployer(undefined);
        setInitBuySniper(undefined);
        setAutoSellTimeLeft(undefined);
    }

    useState(() => {
        if (deployedTokens[deployedTokens.length - 1]) {
            injectToken(deployedTokens[deployedTokens.length - 1]);
        }
    });

    return (
        <div className='flex text-ash-grey'>
            <div className='w-[239px] flex flex-col border border-midnight'>
                <Section title={<span className='text-[14px] font-bold'>Your Deployed Tokens</span>}>
                    <div className='min-h-[92px] flex flex-col text-[14px] p-[11px]'>
                        {[...deployedTokens].reverse().map((token) => (
                            <div key={token.address} onClick={() => injectToken(token)}>
                                <span className='font-medium'>{token.name}</span> | <span className='font-bold'>{token.symbol}</span>
                            </div>
                        ))}
                    </div>
                </Section>
                <Section title={<span className='text-[14px] font-bold'>Your Purchased Tokens</span>}>
                    <div className='min-h-[92px] flex flex-col text-[14px] p-[11px]'>
                        {[...purchasedTokens].reverse().map((token) => (
                            <div key={token.address} onClick={() => injectToken(token)}>
                                <span className='font-medium'>{token.name}</span> | <span className='font-bold'>{token.symbol}</span>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>
            <div className='h-full w-full flex flex-col gap-[16px] p-[26px] border border-x-0 border-b-0 border-midnight'>
                <div className='flex'>
                    {dex && renderDexIcon(dex, { className: 'size-[28px] mr-[19px]' })}
                    <span className='text-[14px] font-bold mr-[16px]'>{mint}</span>
                    <CopyToClipboard className='size-[20px] mr-[7px]' copy={mint} />
                    {mint && (
                        <a href={`https://dexscreener.com/solana/${mint}`} target='_blank' rel='noreferrer'>
                            <DexIcon className='size-[20px]' />
                        </a>
                    )}
                </div>
                <div className='flex'>
                    <div className='size-[85px] flex justify-center items-center'>
                        {imageSrc ? <img className='size-[85px]' src={imageSrc ?? ''} alt='Token icon' /> : <span>image</span>}
                    </div>
                    <div className='flex flex-col text-[14px] ml-[25px]'>
                        {dex && (
                            <div className='flex'>
                                <span className='w-[59px] font-medium mr-[16px] text-right'>Dex:</span>
                                <span className='font-bold'>{dex}</span>
                            </div>
                        )}
                        <div className='flex'>
                            <span className='w-[59px] font-medium mr-[16px] text-right'>Name:</span>
                            <span className='font-bold'>{name}</span>
                        </div>
                        <div className='flex'>
                            <span className='w-[59px] font-medium  mr-[16px] text-right'>Symbol:</span>
                            <span className='font-bold'>{symbol}</span>
                        </div>
                        {initBuyDeployer && (
                            <>
                                <div className='flex'>
                                    <span className='w-[59px] font-medium  mr-[16px] text-right'>Dev Buy:</span>
                                    <span className='font-bold'>{initBuyDeployer}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {autoSellTimeLeft && autoSellTimer && initBuyDeployer && (
                    <div className='flex gap-[38px]'>
                        <span className='text-[14px] font-bold'>Auto Sell</span>
                        <span className='text-[14px] font-medium'>{autoSellTimeLeft}</span>
                        <Cancel onClick={() => setAutoSellTimer(false)} />
                    </div>
                )}
                <div className='flex'>
                    <span className='text-[14px] font-bold mr-[44px]'>Token Value (SOL)</span>
                    <span className='text-[16px] text-accent-lime font-bold mr-[25px]'>{(tokenValueDeployer / LAMPORTS_PER_SOL).toFixed(4)}</span>

                    {initBuyDeployer && (
                        <>
                            <span className='text-[14px] font-bold mr-[44px]'>Profit</span>
                            <span className='text-[16px] text-accent-lime font-bold mr-[25px]'>{`${(
                                tokenValueDeployer / LAMPORTS_PER_SOL -
                                initBuyDeployer
                            ).toFixed(4)} (${(((tokenValueDeployer / LAMPORTS_PER_SOL - initBuyDeployer) / initBuyDeployer) * 100).toFixed(2)}%)`}</span>

                            {autoSellProfit && (
                                <div className='flex gap-[38px]'>
                                    <span className='text-[14px] font-bold'>Auto Sell On Profit</span>
                                    <Cancel onClick={() => setAutoSellProfit(false)} />
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className='flex gap-[10px]'>
                    <Sell percent={100} onClick={() => sell(100, solPrivateKey)} />
                    <Sell percent={50} onClick={() => sell(50, solPrivateKey)} />
                    <Sell percent={25} onClick={() => sell(25, solPrivateKey)} />
                    <Sell percent={10} onClick={() => sell(10, solPrivateKey)} />
                    <Sell percent={5} onClick={() => sell(5, solPrivateKey)} />
                    <Sell percent={1} onClick={() => sell(1, solPrivateKey)} />
                </div>
                <div className='flex items-center'>
                    <span className='text-[14px] font-bold mr-[19px]'>Token Balance</span>
                    <span className='font-medium mr-[28px]'>{tokenBalanceDeployer}</span>
                    <span className='font-medium mr-[13px]'>Transfer</span>
                    <Form.Input
                        className='w-[44px] mr-[9px]'
                        value={transferPercentDeployer}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 100) {
                                setTransferPercentDeployer(value);
                            } else if (value < 0) {
                                setTransferPercentDeployer(0);
                            } else if (value > 100) {
                                setTransferPercentDeployer(100);
                            }
                        }}
                    />
                    <span className='mr-[13px]'>%</span>
                    <span className='mr-[10px]'>to</span>
                    <Form.Input
                        value={transferWalletDeployer}
                        onChange={(e) => {
                            setTransferWalletDeployer(e.target.value);
                        }}
                        className='mr-[16px]'
                        placeholder='Wallet Address'
                    />
                    <Transfer onClick={() => transfer(solPrivateKey, transferWalletDeployer, transferPercentDeployer)} />
                </div>
                <div>
                    <span className='text-[14px] font-bold'>Snipe Wallet</span>
                </div>
                <div className='flex'>
                    <span className='text-[14px] font-bold mr-[44px]'>Token Value (SOL)</span>
                    <span className='text-[16px] text-accent-lime font-bold mr-[25px]'>{(tokenValueSniper / LAMPORTS_PER_SOL).toFixed(4)}</span>

                    {initBuySniper && (
                        <>
                            <span className='text-[14px] font-bold mr-[44px]'>Profit</span>
                            <span className='text-[16px] text-accent-lime font-bold mr-[25px]'>{`${(
                                tokenValueSniper / LAMPORTS_PER_SOL -
                                initBuySniper
                            ).toFixed(4)} (${(((tokenValueSniper / LAMPORTS_PER_SOL - initBuySniper) / initBuySniper) * 100).toFixed(2)}%)`}</span>
                        </>
                    )}
                </div>
                <div className='flex gap-[10px]'>
                    <Sell percent={100} onClick={() => sell(100, solSniperPrivateKey)} />
                    <Sell percent={50} onClick={() => sell(50, solSniperPrivateKey)} />
                    <Sell percent={25} onClick={() => sell(25, solSniperPrivateKey)} />
                    <Sell percent={10} onClick={() => sell(10, solSniperPrivateKey)} />
                    <Sell percent={5} onClick={() => sell(5, solSniperPrivateKey)} />
                    <Sell percent={1} onClick={() => sell(1, solSniperPrivateKey)} />
                </div>
                <div className='flex items-center'>
                    <span className='text-[14px] font-bold mr-[19px]'>Token Balance</span>
                    <span className='font-medium mr-[28px]'>{tokenBalanceSniper}</span>
                    <span className='font-medium mr-[13px]'>Transfer</span>
                    <Form.Input
                        className='w-[44px] mr-[9px]'
                        value={transferPercentSniper}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 && value <= 100) {
                                setTransferPercentSniper(value);
                            } else if (value < 0) {
                                setTransferPercentSniper(0);
                            } else if (value > 100) {
                                setTransferPercentSniper(100);
                            }
                        }}
                    />
                    <span className='mr-[13px]'>%</span>
                    <span className='mr-[10px]'>to</span>
                    <Form.Input
                        value={transferWalletSniper}
                        onChange={(e) => {
                            setTransferWalletSniper(e.target.value);
                        }}
                        className='mr-[16px]'
                        placeholder='Wallet Address'
                    />
                    <Transfer onClick={() => transfer(solSniperPrivateKey, transferWalletSniper, transferPercentSniper)} />
                </div>
            </div>
        </div>
    );
}

function Cancel(props: React.PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement>>) {
    return (
        <Form.Button className='w-[81px] h-[34px] text-[14px] text-ash-grey font-bold' {...props}>
            Cancel
        </Form.Button>
    );
}

function Transfer({ ...props }: {} & React.PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement>>) {
    return (
        <Form.Button className='w-[81px] h-[34px] text-[14px] text-ash-grey font-bold' {...props}>
            Transfer
        </Form.Button>
    );
}

function Sell({ percent, ...props }: { percent: number } & React.PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement>>) {
    return (
        <Form.Button className='w-[102px] h-[34px] text-[14px] text-ash-grey font-bold' {...props}>
            Sell {percent}%
        </Form.Button>
    );
}
